'use client';

import React, {useRef, useState} from 'react';
import {Tooltip} from '@website/components/shared/Tooltip';
import {ShareButtons} from '@website/components/Gallery/ShareButtons';
import {WellniteArtImage} from '@website/types/art.types';
import {CloseIcon} from './CloseIcon';
import {ShareIcon} from './ShareIcon';

interface WellniteArtDetailsProps {
  image: WellniteArtImage;
  onClose?: () => void;
}

export function WellniteArtDetails({
  image,
  onClose,
}: WellniteArtDetailsProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(true);
  const [shareOn, setShareOn] = useState(false);
  const tooltipRef = useRef<Element>(null);

  if (!isOpen) {
    return <></>;
  }

  return (
    <div
      className="text-center flex flex-col justify-center items-center space-y-2 self-center h-full w-full fixed inset-[0px] bg-black bg-opacity-80 z-20"
    >
      <div className="p-10 bg-white md:rounded-[40px] relative overflow-scroll md:overflow-hidden">
        <button
          onClick={() => {
            setIsOpen(false);
            onClose && onClose();
          }}
          className="flex justify-center items-center hover:bg-black h-12 w-12 group absolute bg-white cursor-pointer rounded-full md:top-[20px] top-[2px] right-[20px]"
        >
          <CloseIcon
            strokeColor="text-gray-700 group-hover:text-white"
            strokeWidth={40}
            classes="w-8 h-8"
          />
        </button>
        <div className="flex flex-col md:flex-row justify-center items-center">
          <div className="font-inter text-2xl text-gray-900 md:mr-5">
            <img
              src={image.generatedImageUrl}
              className="rounded-[40px] max-h-[60vh] max-w-[80vw] w-auto md:mr-4 my-2 m-auto"
              alt="Generated Image"
            />
          </div>
          <div className="flex flex-col w-full max-w-[600px] gap-4 justify-center">
            <div className="pt-6 flex gap-2 items-center">
              <Tooltip
                ref={tooltipRef}
                content={
                  <div className="p-1">
                    <ShareButtons
                      id={image._id}
                      title={image.generatedImageTitle}
                    />
                  </div>
                }
                theme="radius-lg"
                visible={shareOn}
                onClickOutside={() => setShareOn(false)}
                interactive
                placement="right"
              >
                <button
                  type="button"
                  className="p-3 flex items-center bg-gray-200 text-black group rounded-full text-base hover:bg-black hover:text-white"
                  onClick={() => setShareOn(true)}
                >
                  <ShareIcon
                    strokeColor="text-black hover:text-white group-hover:text-white"
                    strokeWidth={1.2}
                    classes="w-5 h-5"
                  />
                </button>
              </Tooltip>
            </div>
            <div className="font-inter text-2xl text-gray-900 text-left">
              {image?.generatedImageTitle || '...'}
            </div>
            <div className="font-inter text-sm text-left mb-10">
              {image?.generatedImageCaption || '...'}
            </div>
            <div className="items-left flex flex-wrap gap-3">
              {image?.tags?.map(tag => (
                <span
                  key={tag._id}
                  className="text-black text-sm float-left bg-[#F4FEDE] rounded-full px-4 py-2"
                >
                  #{tag.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
